/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import { TaskPriority } from '../types';
import { X, Plus, Save, Loader2, Search, ChevronDown, Plug, Info, Boxes } from 'lucide-react';
import AlertModal from './AlertModal';
import CustomSelect, { SelectOption } from './CustomSelect';

const CreateTaskModal: React.FC = () => {
    const {
        addTask,
        createExternalTask,
        updateTask,
        myOrgTeams,
        allUsers,
        tasks,
        externalIdentities,
        user,
        userProfile,
        isModalOpen,
        closeModal,
        editingTask,
        showToast,
        fetchTeamProjectMappings,
        getExternalProjectList
    } = useStandUp();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [priority, setPriority] = useState<TaskPriority>('medium');
    const [selectedDeps, setSelectedDeps] = useState<string[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [depSearchQuery, setDepSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showDiscardAlert, setShowDiscardAlert] = useState(false);

    // External Sync State
    const [selectedExtMapping, setSelectedExtMapping] = useState<string>('none');
    const [teamProjectMappings, setTeamProjectMappings] = useState<any[]>([]);

    // Filter assignees to only show members of the SELECTED team
    const filteredAssignees = useMemo(() => {
        let base = allUsers.filter(u => u.teamIds?.includes(selectedTeamId));

        if (selectedExtMapping && selectedExtMapping !== 'none') {
            const provider = selectedExtMapping.split('_')[0];
            base = base.filter(u => {
                const identity = externalIdentities[u.email?.toLowerCase() || ''];
                return !!identity?.mappings?.[provider];
            });
        }
        return base;
    }, [allUsers, selectedTeamId, selectedExtMapping, externalIdentities]);

    // Determine available teams for the dropdown
    const activeTeamIds = useMemo(() => userProfile?.activeTeamIds || [], [userProfile?.activeTeamIds]);

    // We show ALL teams in the Org that the user is a member of
    const availableTeams = useMemo(() => {
        return myOrgTeams || [];
    }, [myOrgTeams]);

    const showTeamSelect = availableTeams.length > 0;

    // Convert teams to SelectOptions (Sorted: Active First, then Alphabetical)
    const teamOptions: SelectOption[] = useMemo(() => {
        const active = availableTeams.filter(t => activeTeamIds.includes(t.id))
            .sort((a, b) => a.name.localeCompare(b.name));

        const others = availableTeams.filter(t => !activeTeamIds.includes(t.id))
            .sort((a, b) => a.name.localeCompare(b.name));

        return [...active, ...others].map(t => ({
            id: t.id,
            label: activeTeamIds.includes(t.id) ? t.name : `${t.name}`
        }));
    }, [availableTeams, activeTeamIds]);

    // Convert filtered assignees to SelectOptions
    const assigneeOptions: SelectOption[] = useMemo(() => {
        const options: SelectOption[] = [
            { id: '', label: 'Unassigned' }
        ];

        // Sort users alphabetically before converting to options
        const sortedUsers = [...filteredAssignees].sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
        );

        sortedUsers.forEach(u => {
            options.push({
                id: u.id,
                label: u.displayName,
                image: u.photoURL || `https://ui-avatars.com/api/?name=${u.displayName}`
            });
        });

        return options;
    }, [filteredAssignees]);

    // Priority Options
    const priorityOptions: SelectOption[] = [
        { id: 'low', label: 'Low' },
        { id: 'medium', label: 'Medium' },
        { id: 'high', label: 'High' },
        { id: 'critical', label: 'Critical' },
    ];

    // Fetch team project mappings and resolve names
    useEffect(() => {
        if (!selectedTeamId || !isModalOpen) {
            setTeamProjectMappings([]);
            return;
        }

        const fetchMappings = async () => {
            const rawMappings = await fetchTeamProjectMappings(selectedTeamId);

            // 1. Show IDs immediately so dropdown doesn't disappear
            const initialMaps = rawMappings.map(m => ({
                id: `${m.provider}_${m.projectId}`,
                provider: m.provider,
                projectId: m.projectId,
                label: `${m.provider.toUpperCase()}: ${m.projectId}`
            }));
            setTeamProjectMappings(initialMaps);

            // 2. Fetch names in the background
            const providers = [...new Set(rawMappings.map(m => m.provider))];

            for (const p of providers) {
                try {
                    const projects = await getExternalProjectList(p as string);
                    if (projects && projects.length > 0) {
                        setTeamProjectMappings(prev => prev.map(m => {
                            if (m.provider !== p) return m;
                            const found = projects.find((proj: any) => proj.id.toString() === m.projectId.toString());
                            return found ? { ...m, label: `${p.toUpperCase()}: ${found.name}` } : m;
                        }));
                    }
                } catch (e) {
                    console.error(`Failed to fetch names for ${p}`, e);
                }
            }
        };

        fetchMappings();
    }, [selectedTeamId, isModalOpen]);

    // Reset or Populate state ONLY on initial open
    useEffect(() => {
        if (isModalOpen) {
            if (editingTask) {
                setTitle(editingTask.title);
                setDescription(editingTask.description || '');
                setAssigneeId(editingTask.assigneeId);
                setPriority(editingTask.priority);
                setSelectedDeps(editingTask.dependencyIds || []);
                setSelectedTeamId(editingTask.teamId);
                setSelectedExtMapping('none'); // Cannot change mapping on existing tasks
            } else {
                setTitle('');
                setDescription('');
                // Default to current user if creating a new task
                setAssigneeId(user?.uid || '');
                setPriority('medium');
                setSelectedDeps([]);

                // Default team: Use the LAST toggled active team
                if (activeTeamIds.length > 0) {
                    setSelectedTeamId(activeTeamIds[activeTeamIds.length - 1]);
                } else if (availableTeams.length > 0) {
                    setSelectedTeamId(availableTeams[0].id);
                } else {
                    setSelectedTeamId('');
                }

                // RESET EXTERNAL MAPPING only on fresh open
                setSelectedExtMapping('none');
            }
        }
        setIsLoading(false);
    }, [isModalOpen]);

    // Reset assignee if they aren't in the newly selected team
    useEffect(() => {
        if (selectedTeamId) {
            if (assigneeId && assigneeId !== '') {
                const isStillValid = filteredAssignees.some(u => u.id === assigneeId);
                if (!isStillValid) {
                    setAssigneeId(''); // Reset to unassigned
                }
            }
        }
    }, [selectedTeamId, filteredAssignees, assigneeId]);

    // Track dirty state
    useEffect(() => {
        if (!isModalOpen) {
            setIsDirty(false);
            return;
        }

        const initialTitle = editingTask?.title || '';
        const initialDesc = editingTask?.description || '';
        const initialAssignee = editingTask ? editingTask.assigneeId : (user?.uid || '');
        const initialPriority = editingTask?.priority || 'medium';
        const initialDeps = editingTask?.dependencyIds || [];
        const initialExtMapping = 'none';

        // Calculate initial team id exactly as initialized
        let initialTeamId = '';
        if (editingTask) {
            initialTeamId = editingTask.teamId;
        } else {
            if (activeTeamIds.length > 0) {
                initialTeamId = activeTeamIds[activeTeamIds.length - 1];
            } else if (availableTeams.length > 0) {
                initialTeamId = availableTeams[0].id;
            }
        }

        const sortedSelected = [...selectedDeps].sort();
        const sortedInitial = [...initialDeps].sort();
        const depsChanged = JSON.stringify(sortedSelected) !== JSON.stringify(sortedInitial);

        const dirty =
            title !== initialTitle ||
            description !== initialDesc ||
            assigneeId !== initialAssignee ||
            priority !== initialPriority ||
            selectedExtMapping !== initialExtMapping ||
            (showTeamSelect && selectedTeamId !== initialTeamId) ||
            depsChanged;

        if (dirty !== isDirty) {
            setIsDirty(dirty);
        }
    }, [title, description, assigneeId, priority, JSON.stringify(selectedDeps), selectedTeamId, selectedExtMapping, editingTask, isModalOpen, user, showTeamSelect, isDirty, availableTeams, activeTeamIds]);

    const dependencyOptions = useMemo(() => {
        return tasks.filter(t => {
            const isExt = 'externalSource' in t;
            if (isExt) return false;
            if ((t as any).teamId !== selectedTeamId) return false;
            if (editingTask && t.id === editingTask.id) return false;
            if (t.status === 'done') return false;
            if (depSearchQuery && !t.title.toLowerCase().includes(depSearchQuery.toLowerCase())) return false;
            return true;
        });
    }, [tasks, selectedTeamId, editingTask, depSearchQuery]);

    const handleClose = () => {
        if (isDirty) {
            setShowDiscardAlert(true);
        } else {
            closeModal();
            setShowDiscardAlert(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (showDiscardAlert) return;

        let finalTeamId = selectedTeamId;
        if (!showTeamSelect && !finalTeamId) {
            finalTeamId = userProfile?.activeTeamId || (availableTeams.length > 0 ? availableTeams[0].id : '');
        }

        if (!title.trim() || !user || !finalTeamId) return;

        setIsLoading(true);

        try {
            if (editingTask) {
                await updateTask(editingTask.id, {
                    title,
                    description,
                    assigneeId,
                    priority,
                    dependencyIds: selectedDeps,
                    teamId: finalTeamId,
                    orgId: userProfile?.orgId || ''
                });
            } else if (selectedExtMapping !== 'none') {
                await createExternalTask({
                    projectKey: selectedExtMapping,
                    title,
                    description,
                    assigneeId,
                    priority
                });
                const provider = selectedExtMapping.split('_')[0];
                showToast(`Task pushed to ${provider.toUpperCase()}!`);
            } else {
                await addTask({
                    title,
                    description,
                    assigneeId: assigneeId || '',
                    reporterId: user.uid,
                    status: 'todo',
                    priority,
                    dependencyIds: selectedDeps,
                    teamId: finalTeamId,
                    orgId: userProfile?.orgId || ''
                });
            }
            closeModal();
        } catch (error: any) {
            console.error("Error saving task:", error);
            showToast(error.message || "Failed to save task", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleDep = (taskId: string) => {
        if (selectedDeps.includes(taskId)) setSelectedDeps(selectedDeps.filter(id => id !== taskId));
        else setSelectedDeps([...selectedDeps, taskId]);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
            }
        };

        if (isModalOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, isDirty]);

    if (!isModalOpen) return null;

    // VALIDATION: Form requires a title to be valid
    const isFormValid = title.trim().length > 0;

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]">

                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center shrink-0">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {editingTask ? 'Edit Task' : 'Create Task'}
                        </h2>
                        <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-200"><X size={20} /></button>
                    </div>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                        <div className="p-4 overflow-y-auto flex-1 flex flex-col gap-4 custom-scrollbar min-h-0 pb-10">

                            {showTeamSelect && (
                                <CustomSelect
                                    label="Team"
                                    options={teamOptions}
                                    value={selectedTeamId}
                                    onChange={(newId) => {
                                        setSelectedTeamId(newId);
                                        if (!editingTask) {
                                            setSelectedExtMapping('none');
                                        }
                                    }}
                                    disabled={isLoading}
                                />
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Title</label>
                                <input
                                    type="text"
                                    className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white"
                                    placeholder="e.g. Implement Auth Endpoint"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    autoFocus
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                                <textarea
                                    className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white resize-none"
                                    placeholder="Add more details about this task..."
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <CustomSelect
                                    label="Assignee"
                                    options={assigneeOptions}
                                    value={assigneeId}
                                    onChange={setAssigneeId}
                                    disabled={isLoading}
                                    placeholder="Unassigned"
                                />
                                <CustomSelect
                                    label="Priority"
                                    options={priorityOptions}
                                    value={priority}
                                    onChange={(val) => setPriority(val as TaskPriority)}
                                    disabled={isLoading}
                                    searchable={false}
                                />
                            </div>

                            {!editingTask && teamProjectMappings.length > 0 && (
                                <div className="space-y-1.5 pt-1">
                                    <label className="text-xs font-bold text-gray-500 uppercase px-1 flex items-center gap-1.5">
                                        <Plug size={12} className="text-brand-500" />
                                        Sync to External Project
                                    </label>
                                    <CustomSelect
                                        options={[
                                            { id: 'none', label: 'None (Local Task only)' },
                                            ...teamProjectMappings
                                        ]}
                                        value={selectedExtMapping}
                                        onChange={setSelectedExtMapping}
                                        disabled={isLoading}
                                    />
                                    {selectedExtMapping !== 'none' && (
                                        <div className="mx-1 mt-1 flex items-start gap-2 p-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg text-[10px] text-indigo-700 dark:text-indigo-300 leading-tight">
                                            <Info size={14} className="shrink-0 mt-0.5" />
                                            <p>This task will be pushed to the provider. Only mapped users appear in the assignee list.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                    Blocking Dependencies
                                    {selectedDeps.length > 0 && (
                                        <span className="ml-2 text-brand-600 dark:text-brand-400 lowercase italic">({selectedDeps.length} selected)</span>
                                    )}
                                </label>
                                <div className="relative group/multi">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Boxes size={16} className="text-gray-400" />
                                    </div>
                                    <div className="w-full">
                                        <CustomSelect
                                            options={dependencyOptions.map(t => ({ id: t.id, label: t.title }))}
                                            value=""
                                            onChange={(id) => toggleDep(id)}
                                            disabled={isLoading}
                                            placeholder={selectedDeps.length === 0 ? "Select blocking tasks..." : `${selectedDeps.length} tasks selected`}
                                        />
                                    </div>
                                </div>

                                {selectedDeps.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2 px-1">
                                        {selectedDeps.map(depId => {
                                            const depTask = tasks.find(t => t.id === depId);
                                            return (
                                                <div key={depId} className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-[10px] font-bold border border-blue-100 dark:border-blue-800">
                                                    <span className="max-w-[120px] truncate">{depTask?.title || depId}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleDep(depId)}
                                                        className="hover:text-red-500 transition-colors"
                                                    >
                                                        <X size={10} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 shrink-0 flex gap-3">
                            <button
                                type="submit"
                                // BUTTON DISABLED LOGIC APPLIED HERE:
                                disabled={isLoading || !isDirty || !isFormValid}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95"
                            >
                                {isLoading ? (
                                    <Loader2 size={18} className="animate-spin" />
                                ) : (
                                    editingTask ? <Save size={18} /> : <Plus size={18} />
                                )}
                                {isLoading ? 'Saving...' : (editingTask ? 'Save Changes' : 'Create Task')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <AlertModal
                isOpen={showDiscardAlert}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to discard them?"
                confirmText="Discard"
                isDestructive={true}
                onConfirm={() => {
                    setShowDiscardAlert(false);
                    closeModal();
                }}
                onCancel={() => setShowDiscardAlert(false)}
            />
        </>
    );
};

export default CreateTaskModal;